/**
 * Swept collide-and-slide resolution for the player capsule.
 *
 * Responsibilities: move the capsule by velocity * dt without passing through
 * solids, apply the contact response for every hit, resolve residual
 * penetration, probe for ground and snap to it, and report lethal contact.
 * It does not decide *how* the player wants to move; that is the motor's job.
 */
import { Vec3 } from '../core/Vec3';
import type { MovementConfig } from '../core/Config';
import { ProximityContact, SweepHit, type CapsuleShape, type CollisionWorld } from '../simulation/CollisionQueries';
import type { SurfaceMaterialDef } from '../world/SurfaceMaterials';
import { carryAcrossSeam } from './SeamCarry';

/** Faces whose normal has at least this much up component (steeper than walkable, not a wall) can veto edge support. */
const STEEP_FACE_MIN_UP = 0.1;

export interface HitRecord {
  readonly point: Vec3;
  readonly normal: Vec3;
  colliderId: number;
  material: SurfaceMaterialDef | null;
  impactSpeed: number;
  /** Tick-time fraction [0, 1] at which the hit occurred. */
  time: number;
}

/** Path vertex (feet position) with its tick-time fraction, for trigger sweeps. */
export interface PathPoint {
  readonly position: Vec3;
  time: number;
}

export interface LethalContact {
  colliderId: number;
  time: number;
  material: SurfaceMaterialDef;
}

/** Called after the default slide response; may modify `velocity`. */
export type ImpactHandler = (normal: Readonly<Vec3>, incoming: Readonly<Vec3>, impactSpeed: number, hit: SweepHit, velocity: Vec3) => void;

const MAX_HITS = 16;

export class MotionResult {
  readonly hits: HitRecord[] = Array.from({ length: MAX_HITS }, () => ({
    point: new Vec3(),
    normal: new Vec3(),
    colliderId: -1,
    material: null,
    impactSpeed: 0,
    time: 0,
  }));
  hitCount = 0;
  readonly path: PathPoint[] = Array.from({ length: MAX_HITS + 4 }, () => ({ position: new Vec3(), time: 0 }));
  pathCount = 0;
  readonly desired = new Vec3();
  readonly resolved = new Vec3();
  iterations = 0;
  depenetration = 0;
  lethal: LethalContact | null = null;
  private readonly lethalScratch: LethalContact = { colliderId: -1, time: 0, material: null as unknown as SurfaceMaterialDef };

  reset(): void {
    this.hitCount = 0;
    this.pathCount = 0;
    this.iterations = 0;
    this.depenetration = 0;
    this.lethal = null;
  }

  setLethal(colliderId: number, time: number, material: SurfaceMaterialDef): void {
    if (this.lethal) return; // first lethal contact wins
    this.lethalScratch.colliderId = colliderId;
    this.lethalScratch.time = time;
    this.lethalScratch.material = material;
    this.lethal = this.lethalScratch;
  }

  pushPath(feet: Readonly<Vec3>, time: number): void {
    if (this.pathCount >= this.path.length) return;
    const p = this.path[this.pathCount++]!;
    p.position.copy(feet);
    p.time = time;
  }

  pushHit(hit: SweepHit, impactSpeed: number, time: number): void {
    if (this.hitCount >= MAX_HITS) return;
    const h = this.hits[this.hitCount++]!;
    h.point.copy(hit.point);
    h.normal.copy(hit.normal);
    h.colliderId = hit.colliderId;
    h.material = hit.material;
    h.impactSpeed = impactSpeed;
    h.time = time;
  }
}

export interface GroundProbeResult {
  grounded: boolean;
  readonly normal: Vec3;
  colliderId: number;
  material: SurfaceMaterialDef | null;
  snapped: number;
}

export class ContactResolver {
  private readonly center = new Vec3();
  private readonly delta = new Vec3();
  private readonly incoming = new Vec3();
  private readonly crease = new Vec3();
  private readonly down = new Vec3();
  private readonly feet = new Vec3();
  private readonly fitCenter = new Vec3();
  private readonly probeShape: CapsuleShape = { radius: 0, halfSegment: 0 };
  private readonly planes: Vec3[] = [new Vec3(), new Vec3(), new Vec3()];
  /**
   * Contact continuity reference planes: surfaces in resting contact at the start
   * of the move, planes hit earlier this tick, and the previous ground plane.
   */
  private readonly refs: Vec3[] = Array.from({ length: 8 }, () => new Vec3());
  private refCount = 0;
  private readonly seamScratch = { t: new Vec3(), k: new Vec3() };
  private readonly hit = new SweepHit();
  private readonly contactBuf: ProximityContact[] = [];
  readonly ground: GroundProbeResult = { grounded: false, normal: new Vec3(0, 1, 0), colliderId: -1, material: null, snapped: 0 };

  constructor(
    private readonly world: CollisionWorld,
    readonly shape: CapsuleShape,
    private readonly cfg: Readonly<MovementConfig>,
  ) {}

  /** Distance from the feet to the capsule segment centre. */
  get centerOffset(): number {
    return this.shape.halfSegment + this.shape.radius;
  }

  private toCenter(feet: Readonly<Vec3>, up: Readonly<Vec3>, out: Vec3): Vec3 {
    return out.copy(feet).addScaled(up, this.centerOffset);
  }

  private toFeet(center: Readonly<Vec3>, up: Readonly<Vec3>, out: Vec3): Vec3 {
    return out.copy(center).addScaled(up, -this.centerOffset);
  }

  /**
   * Moves `position` (feet) by `velocity * dt`, sliding along solids.
   * `velocity` is updated with the contact response. Stops at the first lethal hit.
   */
  move(position: Vec3, velocity: Vec3, up: Readonly<Vec3>, dt: number, onImpact: ImpactHandler | null, out: MotionResult): void {
    const cfg = this.cfg;
    const c = this.toCenter(position, up, this.center);
    out.desired.copy(velocity).scale(dt);
    out.pushPath(position, 0);
    let planeCount = 0;
    const continuity = cfg.contactContinuityDeg > 0;
    this.refCount = 0;
    if (continuity) this.collectResting(c, up);
    let timeLeft = 1;
    let timeUsed = 0;

    for (let iter = 0; iter < cfg.maxSlideIterations; iter++) {
      this.delta.copy(velocity).scale(dt * timeLeft);
      const len = this.delta.length();
      if (len < 1e-9) break;
      out.iterations = iter + 1;
      if (!this.world.sweepCapsule(c, up, this.delta, this.shape, 0, this.hit)) {
        c.add(this.delta);
        timeUsed = 1;
        out.pushPath(this.toFeet(c, up, this.feet), 1);
        break;
      }
      const hit = this.hit;
      const n = hit.normal;
      if (hit.fraction <= 1e-5 && this.delta.dot(n) > -1e-6 * len && hit.material?.kind !== 'lethal') {
        // Already inside the skin band but not moving into the surface (shape-cast
        // TOI is only accurate to ~1 mm near contact). Restore the gap and retry.
        c.addScaled(n, cfg.skin);
        continue;
      }
      const hitTime = timeUsed + timeLeft * hit.fraction;
      // Advance to the time of impact, then restore the skin gap along the normal.
      c.addScaled(this.delta, hit.fraction).addScaled(n, cfg.skin);
      timeUsed = hitTime;
      timeLeft = 1 - hitTime;
      out.pushPath(this.toFeet(c, up, this.feet), hitTime);

      if (hit.material?.kind === 'lethal') {
        out.pushHit(hit, Math.max(0, -velocity.dot(n)), hitTime);
        out.setLethal(hit.colliderId, hitTime, hit.material);
        break;
      }
      this.incoming.copy(velocity);
      if (continuity && velocity.dot(n) < 0) this.carryIfSliding(velocity, n);
      // After a seam carry, only the part of the velocity aimed into the new surface is an impact:
      // impact rules get the pre-carry velocity as `incoming` and that remainder as the speed. A seam
      // crossed with no remainder is not an impact, so impact rules do not run for it.
      const vn = velocity.dot(n);
      out.pushHit(hit, Math.max(0, -vn), hitTime);
      if (vn < 0) {
        velocity.addScaled(n, -vn);
        onImpact?.(n, this.incoming, -vn, hit, velocity);
      }
      // Multi-plane handling: keep the velocity out of every plane touched this tick.
      if (planeCount < this.planes.length) this.planes[planeCount++]!.copy(n);
      if (continuity) this.addRef(n);
      this.clipAgainstPlanes(velocity, planeCount);
      if (timeLeft <= 1e-6) break;
    }
    if (timeUsed < 1 && !out.lethal && out.iterations >= cfg.maxSlideIterations) {
      // Iteration budget exhausted inside a tight crease: remaining motion is dropped
      // (the position is still valid). Counted in diagnostics.
      out.pushPath(this.toFeet(c, up, this.feet), 1);
    }
    if (!out.lethal) this.maintainSkin(c, velocity, up, out);
    this.toFeet(c, up, position);
    out.resolved.copy(position);
  }

  private addRef(n: Readonly<Vec3>): void {
    for (let i = 0; i < this.refCount; i++) if (this.refs[i]!.dot(n) > 1 - 1e-9) return;
    // When full, the newest reference (a hit this tick or the previous ground)
    // replaces the last resting contact: it is the likelier sliding plane.
    if (this.refCount < this.refs.length) this.refs[this.refCount++]!.copy(n);
    else this.refs[this.refs.length - 1]!.copy(n);
  }

  /** Adds the normals of non-lethal surfaces within two skins of the capsule at `c` as reference planes. */
  private collectResting(c: Readonly<Vec3>, up: Readonly<Vec3>): void {
    const count = this.world.contacts(c, up, this.shape, this.cfg.skin * 2, this.contactBuf);
    for (let i = 0; i < count; i++) {
      const k = this.contactBuf[i]!;
      if (k.material?.kind !== 'lethal' && k.distance <= this.cfg.skin * 2) this.addRef(k.normal);
    }
  }

  /**
   * Contact continuity. If the velocity is sliding along a surface p (resting at
   * tick start, hit earlier this tick, or the previous ground) whose normal is within the seam angle
   * of the new normal n, the part tangent to p is rotated by the rotation taking
   * p to n: it keeps its speed and becomes tangent to n. The part pushing into p
   * (steering into the wall) is not carried and is lost exactly as before.
   * Speed never increases.
   */
  private carryIfSliding(velocity: Vec3, n: Readonly<Vec3>): void {
    const speed = velocity.length();
    if (speed < 1e-9) return;
    const cosSeam = Math.cos((this.cfg.contactContinuityDeg * Math.PI) / 180);
    // The reference is the plane the velocity is sliding along: the most tangent
    // one within the seam angle of n, and tangent to within 5 % of the speed
    // (|v̂·p| ≤ 0.05, about 3°). Steering into a wall exceeds that, so the seam
    // is then inelastic as in Phase 2.
    let best: Vec3 | null = null;
    let bestAlign = 0.05;
    for (let i = 0; i < this.refCount; i++) {
      const p = this.refs[i]!;
      const cos = p.dot(n);
      const align = Math.abs(velocity.dot(p)) / speed;
      if (cos >= cosSeam && cos < 1 - 1e-9 && align <= bestAlign) {
        best = p;
        bestAlign = align;
      }
    }
    if (best) carryAcrossSeam(velocity, best, n, velocity, this.seamScratch);
  }

  private clipAgainstPlanes(velocity: Vec3, planeCount: number): void {
    for (let i = 0; i < planeCount; i++) {
      const pi = this.planes[i]!;
      if (velocity.dot(pi) >= -1e-9) continue;
      velocity.projectOnPlane(pi);
      for (let j = 0; j < planeCount; j++) {
        if (j === i) continue;
        const pj = this.planes[j]!;
        if (velocity.dot(pj) >= -1e-9) continue;
        // Two opposing planes: slide along their crease line.
        this.crease.crossVectors(pi, pj);
        const cl = this.crease.length();
        if (cl < 1e-6) {
          velocity.set(0, 0, 0);
          return;
        }
        this.crease.scale(1 / cl);
        const along = velocity.dot(this.crease);
        velocity.copy(this.crease).scale(along);
        for (let k = 0; k < planeCount; k++) {
          if (k !== i && k !== j && velocity.dot(this.planes[k]!) < -1e-9) {
            velocity.set(0, 0, 0); // boxed in by three planes
            return;
          }
        }
        return;
      }
    }
  }

  /**
   * Restores the skin gap using exact contact distances (shape-cast TOI drifts
   * by ~1 mm near contact) and pushes out of residual penetration. Surfaces
   * closer than half the skin are pushed back to the full skin distance.
   */
  private maintainSkin(c: Vec3, velocity: Vec3, up: Readonly<Vec3>, out: MotionResult): void {
    const minGap = this.cfg.skin * 0.5;
    for (let iter = 0; iter < this.cfg.maxDepenetrationIterations; iter++) {
      const count = this.world.contacts(c, up, this.shape, minGap, this.contactBuf);
      let closest: ProximityContact | null = null;
      for (let i = 0; i < count; i++) {
        const k = this.contactBuf[i]!;
        if (k.distance < minGap && (!closest || k.distance < closest.distance)) closest = k;
      }
      if (!closest) return;
      if (closest.material?.kind === 'lethal') {
        out.setLethal(closest.colliderId, 1, closest.material);
        return;
      }
      c.addScaled(closest.normal, this.cfg.skin - closest.distance);
      if (closest.distance < 0) out.depenetration -= closest.distance;
      if (this.cfg.contactContinuityDeg > 0 && velocity.dot(closest.normal) < 0) this.carryIfSliding(velocity, closest.normal);
      const vn = velocity.dot(closest.normal);
      if (vn < 0) velocity.addScaled(closest.normal, -vn);
    }
  }

  /**
   * Ground detection. Sweeps down along -up. When `allowSnap`, the player is
   * pulled down onto walkable ground within `snapDistance` provided the
   * velocity's separation from that ground plane is small (see MOVEMENT_SPEC.md).
   */
  probeGround(position: Vec3, velocity: Vec3, up: Readonly<Vec3>, allowSnap: boolean, out: MotionResult, previousGround: Readonly<Vec3> | null = null): GroundProbeResult {
    // Contact continuity: the probe follows this tick's move and keeps its reference
    // planes, plus the plane stood on last tick (ground seams).
    if (previousGround && this.cfg.contactContinuityDeg > 0) this.addRef(previousGround);
    const result = this.detectGround(position, velocity, up, allowSnap, out);
    if (result.snapped > 0 && !out.lethal) {
      const c = this.toCenter(position, up, this.center);
      this.maintainSkin(c, velocity, up, out);
      this.toFeet(c, up, position);
    }
    return result;
  }

  private detectGround(position: Vec3, velocity: Vec3, up: Readonly<Vec3>, allowSnap: boolean, out: MotionResult): GroundProbeResult {
    const cfg = this.cfg;
    const g = this.ground;
    g.grounded = false;
    g.colliderId = -1;
    g.material = null;
    g.snapped = 0;
    const cosMax = Math.cos((cfg.maxGroundAngleDeg * Math.PI) / 180);
    const c = this.toCenter(position, up, this.center);

    // 1. Resting contact: exact distances to walkable surfaces within the probe distance.
    // Internal edges: where solids meet flush (tiled ramps, a ramp foot on a floor) the
    // probe also reports the neighbour's edge, with a blended normal that leans back
    // toward the player. Choosing it would snap the capsule down onto the edge and
    // redirect the velocity off the real surface (a hop at every seam). So the
    // supporting contact is chosen among the nearest ones (within one skin), faces
    // before edges, then flattest. A lone edge (standing on a crest) still counts,
    // but not an edge lying beyond a steep upward-facing face the capsule touches
    // more closely: on a faceted concave curve the edge of a lower facet then
    // reports a walkable normal leaning toward the player, and supporting on it
    // threw the player off the curve (research/HIGH_SPEED_GEOMETRY.md, D1). Walls
    // and ceilings do not veto, so standing on a ledge edge beside a wall works.
    const count = this.world.contacts(c, up, this.shape, cfg.groundProbeDistance, this.contactBuf);
    let nearest = Infinity;
    let nearestSteepFace = Infinity;
    for (let i = 0; i < count; i++) {
      const k = this.contactBuf[i]!;
      if (k.normal.dot(up) >= cosMax) nearest = Math.min(nearest, k.distance);
      if (k.onFace && k.normal.dot(up) > STEEP_FACE_MIN_UP) nearestSteepFace = Math.min(nearestSteepFace, k.distance);
    }
    let best: ProximityContact | null = null;
    for (let i = 0; i < count; i++) {
      const k = this.contactBuf[i]!;
      const nd = k.normal.dot(up);
      if (nd < cosMax || k.distance > nearest + cfg.skin) continue;
      if (!k.onFace && k.distance > nearestSteepFace + cfg.skin) continue;
      if (!best || (k.onFace && !best.onFace) || (k.onFace === best.onFace && nd > best.normal.dot(up))) best = k;
    }
    if (best) {
      const separation = velocity.dot(best.normal);
      if (separation > cfg.groundSeparationTolerance) return g; // leaving the surface (crest, jump)
      if (best.material?.kind === 'lethal') out.setLethal(best.colliderId, 1, best.material);
      let drop = Math.max(0, best.distance - cfg.skin) / best.normal.dot(up);
      if (drop > 0) {
        // The drop is straight down, not along the contact normal, so it can pass
        // through a steep edge beside the capsule (rolling off a low ledge onto the
        // floor below, research/EMERGENT_TECH.md T7). Sweep it and stop short of that.
        // The supporting collider itself is excluded: the drop is no longer than the
        // vertical gap to it, so a hit on it would only be TOI drift. A limited drop
        // still reports grounded; the next tick's move clears the edge and drops again.
        this.down.copy(up).scale(-drop);
        if (this.world.sweepCapsule(c, up, this.down, this.shape, 0, this.hit) && this.hit.colliderId !== best.colliderId) {
          drop = Math.min(drop, Math.max(0, this.hit.fraction * drop - cfg.skin));
        }
      }
      if (drop > 0) {
        c.addScaled(up, -drop);
        this.toFeet(c, up, position);
        g.snapped = drop;
      }
      if (separation < 0 && this.refCount > 0) this.carryIfSliding(velocity, best.normal);
      const into = velocity.dot(best.normal);
      if (into < 0) velocity.addScaled(best.normal, -into);
      return this.fillGround(best.normal, best.colliderId, best.material);
    }
    if (!allowSnap) return g;

    // 2. Snap: follow walkable ground that dropped away by up to snapDistance.
    this.down.copy(up).scale(-cfg.snapDistance);
    if (!this.world.sweepCapsule(c, up, this.down, this.shape, 0, this.hit)) return g;
    const hit = this.hit;
    if (hit.normal.dot(up) < cosMax) return g;
    if (velocity.dot(hit.normal) > cfg.snapMaxSeparationSpeed) return g;
    if (hit.material?.kind === 'lethal') return g; // never snap onto a lethal surface
    const drop = Math.max(0, hit.fraction * cfg.snapDistance - cfg.skin);
    c.addScaled(up, -drop);
    this.toFeet(c, up, position);
    g.snapped = drop;
    // Redirect onto the new ground plane, preserving speed (see MOVEMENT_SPEC.md).
    const speed = velocity.length();
    velocity.projectOnPlane(hit.normal);
    const l = velocity.length();
    if (l > 1e-9) velocity.scale(speed / l);
    return this.fillGround(hit.normal, hit.colliderId, hit.material);
  }

  private fillGround(normal: Readonly<Vec3>, colliderId: number, material: SurfaceMaterialDef | null): GroundProbeResult {
    const g = this.ground;
    g.grounded = true;
    g.normal.copy(normal);
    g.colliderId = colliderId;
    g.material = material;
    return g;
  }

  /**
   * True when a capsule of this radius and half segment, with its feet at
   * `position`, touches no solid (crouch → stand checks). The resolver's own
   * shape is not changed.
   */
  fits(position: Readonly<Vec3>, up: Readonly<Vec3>, halfSegment: number): boolean {
    const shape = this.probeShape;
    shape.radius = this.shape.radius;
    shape.halfSegment = halfSegment;
    const c = this.fitCenter.copy(position).addScaled(up, halfSegment + shape.radius);
    return this.world.contacts(c, up, shape, 0, this.contactBuf) === 0;
  }

  /** Collects solids within `distance` of the capsule at `position` (feet). */
  nearby(position: Readonly<Vec3>, up: Readonly<Vec3>, distance: number, out: ProximityContact[]): number {
    const c = this.toCenter(position, up, this.center);
    return this.world.contacts(c, up, this.shape, distance, out);
  }
}
